(() => {
  let syncing = false;
  let queued = false;
  let scheduled = false;
  const observedShadowRoots = new WeakSet();

  function scheduleSync(delay = 50) {
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      syncOnce();
    }, delay);
  }

  async function fetchCart() {
    const res = await fetch(`${Shopify.routes.cart}.js`, { credentials: "same-origin" });
    if (!res.ok) throw new Error(res.statusText);
    return res.json();
  }

  async function removeByKey(key) {
    const body = new URLSearchParams();
    body.set("id", key);
    body.set("quantity", "0");
    await fetch(`${Shopify.routes.cart_change}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
      credentials: "same-origin",
    });
  }

  /** UpCart keeps its own drawer DOM; re-fetch server cart after fee line add/remove. */
  function notifyCartUiAfterHandlingFeeSync(cart) {
    const tryUpcart = () => {
      if (typeof window.upcartRefreshCart === "function") {
        try {
          window.upcartRefreshCart();
          return true;
        } catch {
          // ignore
        }
      }
      return false;
    };

    queueMicrotask(() => {
      if (!tryUpcart()) {
        let n = 0;
        const id = setInterval(() => {
          n += 1;
          if (tryUpcart() || n >= 25) clearInterval(id);
        }, 80);
      }

      try {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:updated", { bubbles: true, detail: cart }),
        );
      } catch {
        // ignore
      }

      try {
        if (window.Cart && typeof window.Cart.fetchTotals === "function") {
          window.Cart.fetchTotals(false);
        }
      } catch {
        // ignore
      }
    });
  }

  /** Handling fee is for the 3-bottle bundle; remove fee when main product units drop below 3. */
  const MIN_MAIN_UNITS_FOR_HANDLING_FEE = 3;

  function productIdForHandlingFee(cart, props) {
    const fromProp = props._pi_handling_fee_product_id;
    if (fromProp != null && String(fromProp).trim() !== "") {
      return String(fromProp);
    }
    const forVariant = props._pi_handling_fee_for;
    if (!forVariant) return null;
    const line = cart.items.find((it) => String(it.variant_id) === String(forVariant));
    return line ? String(line.product_id) : null;
  }

  function totalNonFeeUnitsForProduct(cart, productId) {
    let n = 0;
    for (const it of cart.items) {
      const p = it.properties || {};
      if (p._pi_handling_fee === "true") continue;
      if (String(it.product_id) !== String(productId)) continue;
      n += it.quantity;
    }
    return n;
  }

  function gatherFeeVariantIdByProductId(cart) {
    const out = {};
    const fromSettings = window.__piHandlingFeeByProductId;
    if (fromSettings && typeof fromSettings === "object") {
      for (const k of Object.keys(fromSettings)) {
        if (!k || k === "undefined") continue;
        out[String(k)] = String(fromSettings[k]);
      }
    }
    for (const it of cart.items) {
      const p = it.properties || {};
      if (p._pi_handling_fee === "true") continue;
      const child = p._pi_handling_fee_child;
      if (child != null && String(child).trim() !== "") {
        out[String(it.product_id)] = String(child);
      }
    }
    return out;
  }

  function hasHandlingFeeLineForProduct(cart, productId) {
    for (const it of cart.items) {
      const props = it.properties || {};
      if (props._pi_handling_fee !== "true") continue;
      const pid = productIdForHandlingFee(cart, props);
      if (pid != null && String(pid) === String(productId)) return true;
    }
    return false;
  }

  function firstMainVariantIdForProduct(cart, productId) {
    for (const it of cart.items) {
      const p = it.properties || {};
      if (p._pi_handling_fee === "true") continue;
      if (String(it.product_id) !== String(productId)) continue;
      return String(it.variant_id);
    }
    return null;
  }

  async function addHandlingFeeLine(productId, feeVariantId, parentVariantId) {
    const payload = {
      items: [
        {
          id: Number(feeVariantId),
          quantity: 1,
          properties: {
            _pi_handling_fee: "true",
            _pi_handling_fee_for: String(parentVariantId),
            _pi_handling_fee_product_id: String(productId),
          },
        },
      ],
    };
    if (window.Cart && window.Cart.sections && window.Cart.sections.length) {
      payload.sections = window.Cart.sections.join(",");
    }
    const res = await fetch(`${Shopify.routes.cart_add}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(res.statusText);
    const data = await res.json();
    if (data?.sections && window.Cart && typeof window.Cart.updateAllHtml === "function") {
      try {
        window.Cart.fetchTotals?.();
        window.Cart.updateAllHtml(data.sections, true);
      } catch {
        // ignore
      }
    }
    return data;
  }

  async function syncOnce() {
    if (syncing) {
      queued = true;
      return;
    }

    syncing = true;
    try {
      const cart = await fetchCart();
      const parentVariantIds = new Set(cart.items.map((it) => String(it.variant_id)));
      const groupsWithParents = new Set(
        cart.items
          .filter((it) => {
            const props = it.properties || {};
            // any non-fee item that has a group token counts as a parent
            return props._pi_handling_fee_group && props._pi_handling_fee !== "true";
          })
          .map((it) => String((it.properties || {})._pi_handling_fee_group)),
      );

      const feeItemsToRemove = cart.items.filter((it) => {
        const props = it.properties || {};
        if (props._pi_handling_fee !== "true") return false;

        const productId = productIdForHandlingFee(cart, props);
        if (
          productId != null &&
          totalNonFeeUnitsForProduct(cart, productId) < MIN_MAIN_UNITS_FOR_HANDLING_FEE
        ) {
          return true;
        }

        // Preferred: group token match (removes fee when its specific parent line is gone).
        if (props._pi_handling_fee_group) {
          return !groupsWithParents.has(String(props._pi_handling_fee_group));
        }

        // Fallback for older items already in cart: variant-id link.
        const parent = props._pi_handling_fee_for;
        if (!parent) return false;
        return !parentVariantIds.has(String(parent));
      });

      let removedFeeLine = false;
      for (const item of feeItemsToRemove) {
        await removeByKey(item.key);
        removedFeeLine = true;
      }

      let cartWork = cart;
      if (removedFeeLine) {
        try {
          cartWork = await fetchCart();
        } catch {
          // ignore
        }
      }

      const feeVariantByProduct = gatherFeeVariantIdByProductId(cartWork);
      let addedFeeLine = false;
      const productIds = new Set(
        cartWork.items
          .filter((it) => (it.properties || {})._pi_handling_fee !== "true")
          .map((it) => String(it.product_id)),
      );

      for (const productId of productIds) {
        if (totalNonFeeUnitsForProduct(cartWork, productId) < MIN_MAIN_UNITS_FOR_HANDLING_FEE) continue;
        if (hasHandlingFeeLineForProduct(cartWork, productId)) continue;
        const feeVid = feeVariantByProduct[productId];
        if (!feeVid) continue;
        const parentVid = firstMainVariantIdForProduct(cartWork, productId);
        if (!parentVid) continue;
        try {
          await addHandlingFeeLine(productId, feeVid, parentVid);
          addedFeeLine = true;
          cartWork = await fetchCart();
        } catch {
          // silent: inventory / network
        }
      }

      if (removedFeeLine || addedFeeLine) {
        notifyCartUiAfterHandlingFeeSync(cartWork);
      }
    } catch {
      // silent: never break cart UX
    } finally {
      syncing = false;
      if (queued) {
        queued = false;
        queueMicrotask(syncOnce);
      }
    }
  }

  // Theme emits this after AJAX cart updates.
  window.addEventListener("theme:cart:updated", () => scheduleSync(0));

  // UpCart / apps may not emit theme events. Patch fetch to react after cart mutations.
  const origFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await origFetch(...args);
    try {
      const url = String(args?.[0]?.url || args?.[0] || "");
      if (url.includes("/cart/add") || url.includes("/cart/change") || url.includes("/cart/update")) {
        scheduleSync(80);
      }
    } catch {
      // ignore
    }
    return res;
  };

  // UpCart often uses XMLHttpRequest instead of fetch.
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
    this.__pi_cartUrl = url;
    return origOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function send(body) {
    try {
      const url = String(this.__pi_cartUrl || "");
      if (url.includes("/cart/add") || url.includes("/cart/change") || url.includes("/cart/update")) {
        this.addEventListener(
          "loadend",
          () => {
            scheduleSync(120);
          },
          { once: true },
        );
      }
    } catch {
      // ignore
    }
    return origSend.call(this, body);
  };

  // UpCart can update UI without hitting Shopify cart endpoints immediately.
  // Observe UpCart shadow roots and trigger sync on product-row changes.
  function observeUpcartShadowRoots() {
    document.querySelectorAll("*").forEach((el) => {
      const sr = el.shadowRoot;
      if (!sr || observedShadowRoots.has(sr)) return;
      if (!sr.querySelector(".upcart-product-item")) return;

      observedShadowRoots.add(sr);
      const mo = new MutationObserver(() => scheduleSync(0));
      mo.observe(sr, { childList: true, subtree: true });
    });
  }

  observeUpcartShadowRoots();
  const rootObserver = new MutationObserver(() => observeUpcartShadowRoots());
  rootObserver.observe(document.documentElement, { childList: true, subtree: true });
})();

